"use client"

import * as React from "react"
import * as ProgressPrimitive from "@radix-ui/react-progress"
import { cn } from "@/lib/utils"
import { StatusAnimation } from "@/components/status-animation"

interface EnhancedProgressProps extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  value?: number
  status?: string
  className?: string
  animateStatus?: boolean
  showPercentage?: boolean
}

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  EnhancedProgressProps
>(({ className, value, status, animateStatus = false, showPercentage = true, ...props }, ref) => (
  <div className={cn("w-full space-y-3", className)}>
    {/* Status and percentage row */}
    <div className="flex items-center justify-between">
      {animateStatus && status ? (
        <StatusAnimation
          currentStatus={status}
          isAnimating={true}
          fontSize="text-sm"
          fontFamily="font-mono"
          className="text-gray-600"
        />
      ) : (
        <span className="text-gray-600 text-sm font-mono">
          {status || "Processing..."}
        </span>
      )}
      {showPercentage && value !== undefined && (
        <span className="text-gray-600 text-sm font-mono">
          {Math.round(value)}%
        </span>
      )}
    </div>
    
    {/* Progress bar */}
    <ProgressPrimitive.Root
      ref={ref}
      className={cn(
        "relative h-2 w-full overflow-hidden rounded-full bg-gray-200",
        className
      )}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className="h-full w-full flex-1 bg-violet-600 transition-all duration-500 ease-out"
        style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
      />
    </ProgressPrimitive.Root>
  </div>
))

Progress.displayName = ProgressPrimitive.Root.displayName

export { Progress }
